import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SchoolProvider } from './contexts/SchoolContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { theme } from './theme';
import { queryClient } from './lib/queryClient';
import { useColorScheme } from './hooks/useColorScheme';
import LoginForm from './components/auth/LoginForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminLayout from './components/layout/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import Schools from './pages/admin/Schools';
import SchoolDetail from './pages/admin/SchoolDetail';
import SchoolForm from './pages/admin/SchoolForm';
import Students from './pages/admin/Students';
import StudentDetail from './pages/admin/StudentDetail';
import StudentForm from './pages/admin/StudentForm';
import Cameras from './pages/admin/Cameras';
import CameraDetail from './pages/admin/CameraDetail';
import CameraForm from './pages/admin/CameraForm';
import Areas from './pages/admin/Areas';
import Activities from './pages/admin/Activities';
import DetectionVerification from './pages/admin/DetectionVerification';
import Alerts from './pages/admin/Alerts';
import Reports from './pages/admin/Reports';
import ParentLayout from './components/layout/ParentLayout';
import ParentDashboard from './pages/parent/ParentDashboard';
import ChildActivities from './pages/parent/ChildActivities';
import ChildPhotos from './pages/parent/ChildPhotos';
import NotificationSettings from './pages/parent/NotificationSettings';
import DailyReports from './pages/parent/DailyReports';
import TeacherLayout from './components/layout/TeacherLayout';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import ClassRoster from './pages/teacher/ClassRoster';
import ActivityVerification from './pages/teacher/ActivityVerification';
import TeacherDailyReports from './pages/teacher/DailyReports';
import StudentProgress from './pages/teacher/StudentProgress';
import TeacherMessages from './pages/teacher/Messages';
import TeacherAttendance from './pages/teacher/Attendance';
import Settings from './pages/admin/Settings';
import Users from './pages/admin/Users';
import ParentSettings from './pages/parent/Settings';
import ChildProfile from './pages/parent/ChildProfile';
import ChildCamera from './pages/parent/ChildCamera';
import ChildAIProgress from './pages/parent/ChildAIProgress';
import AuthDebugPanel from './components/debug/AuthDebugPanel';

// Import Mantine styles
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/spotlight/styles.css';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, _errorInfo: React.ErrorInfo) {
    // Error logged internally
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', backgroundColor: '#fee', color: '#c00' }}>
          <h1>Something went wrong.</h1>
          <pre>{this.state.error?.message}</pre>
          <pre>{this.state.error?.stack}</pre>
        </div>
      );
    }

    return this.props.children;
  }
}

// Create Mantine theme
const mantineTheme = createTheme(theme);

function App() {
  const { colorScheme } = useColorScheme();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MantineProvider
          theme={mantineTheme}
          defaultColorScheme={colorScheme}
        >
          <Notifications position="top-right" />
          <ModalsProvider>
            <AuthProvider>
              <SchoolProvider>
                <WebSocketProvider>
                  <AppRoutes />
                  <AuthDebugPanel />
                </WebSocketProvider>
              </SchoolProvider>
            </AuthProvider>
          </ModalsProvider>
        </MantineProvider>
        {/* {config.debug.enabled && <ReactQueryDevtools initialIsOpen={false} />} */}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Login route wrapper that redirects authenticated users
const LoginRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginForm />;
};

// Role-based dashboard redirect component
const DashboardRedirect = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Get user role from either role_info or role field
  const userRole = user.role_info?.value || user.role;

  // If user exists but role is not yet available, show loading
  if (!userRole) {
    if (import.meta.env.DEV) {
      console.warn('DashboardRedirect: User exists but no role available', { user });
    }
    return <div>Loading user permissions...</div>;
  }

  // Redirect to appropriate dashboard based on user role
  switch (userRole) {
    case 'super_admin':
    case 'admin':
      return <Navigate to="/admin/dashboard" replace />;
    case 'teacher':
      return <Navigate to="/teacher/dashboard" replace />;
    case 'parent':
      return <Navigate to="/parent/dashboard" replace />;
    case 'viewer':
      return <Navigate to="/viewer/dashboard" replace />;
    case 'operator':
      return <Navigate to="/operator/dashboard" replace />;
    default:
      // For unknown roles, redirect to login
      if (import.meta.env.DEV) {
        console.warn('DashboardRedirect: Unknown role', { userRole, user });
      }
      return <Navigate to="/login" replace />;
  }
};

// Separate routes component to use auth context
const AppRoutes = () => {

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginRoute />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={<DashboardRedirect />}
        />

        {/* Admin Dashboard - Explicit route for admins */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
              <AdminLayout>
                <Dashboard />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* School Management Routes */}
        <Route
          path="/admin/schools"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Schools />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/schools/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <SchoolDetail />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/schools/:id/edit"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <SchoolForm />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Student Management Routes */}
        <Route
          path="/admin/students"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Students />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/students/new"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <StudentForm />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/students/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <StudentDetail />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/students/:id/edit"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <StudentForm />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Camera Management Routes */}
        <Route
          path="/admin/cameras"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Cameras />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/cameras/new"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CameraForm />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/cameras/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CameraDetail />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/cameras/:id/edit"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CameraForm />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Area Management Routes */}
        <Route
          path="/admin/areas"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Areas />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/school/areas"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Areas />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Activities/Detection Management Routes */}
        <Route
          path="/admin/activities"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Activities />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/activities/detection/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <DetectionVerification />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/alerts"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Alerts />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Reports />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Parent Portal Routes */}
        <Route
          path="/parent/dashboard"
          element={
            <ProtectedRoute allowedRoles={['parent', 'viewer']}>
              <ParentLayout>
                <ParentDashboard />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/child/:childId"
          element={
            <ProtectedRoute allowedRoles={['parent', 'viewer']}>
              <ParentLayout>
                <ChildProfile />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/child/:childId/activities"
          element={
            <ProtectedRoute>
              <ParentLayout>
                <ChildActivities />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/child/:childId/photos"
          element={
            <ProtectedRoute>
              <ParentLayout>
                <ChildPhotos />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/child/:childId/camera"
          element={
            <ProtectedRoute allowedRoles={['parent', 'viewer']}>
              <ParentLayout>
                <ChildCamera />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/child/:childId/ai-progress"
          element={
            <ProtectedRoute allowedRoles={['parent', 'viewer']}>
              <ParentLayout>
                <ChildAIProgress />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/child/:childId/reports"
          element={
            <ProtectedRoute>
              <ParentLayout>
                <DailyReports />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/settings"
          element={
            <ProtectedRoute>
              <ParentLayout>
                <ParentSettings />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/notifications"
          element={
            <ProtectedRoute>
              <ParentLayout>
                <NotificationSettings />
              </ParentLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/daily-reports"
          element={
            <ProtectedRoute>
              <ParentLayout>
                <DailyReports />
              </ParentLayout>
            </ProtectedRoute>
          }
        />

        {/* Teacher Portal Routes */}
        <Route
          path="/teacher/dashboard"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin', 'super_admin']}>
              <TeacherLayout>
                <TeacherDashboard />
              </TeacherLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/students"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin', 'super_admin']}>
              <TeacherLayout>
                <ClassRoster />
              </TeacherLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/attendance"
          element={
            <ProtectedRoute>
              <TeacherLayout>
                <TeacherAttendance />
              </TeacherLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/verify"
          element={
            <ProtectedRoute>
              <TeacherLayout>
                <ActivityVerification />
              </TeacherLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/reports"
          element={
            <ProtectedRoute>
              <TeacherLayout>
                <TeacherDailyReports />
              </TeacherLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/progress"
          element={
            <ProtectedRoute>
              <TeacherLayout>
                <StudentProgress />
              </TeacherLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/messages"
          element={
            <ProtectedRoute>
              <TeacherLayout>
                <TeacherMessages />
              </TeacherLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin Settings & Users Routes */}
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Settings />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Users />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Viewer Routes - Using ParentLayout */}
        <Route
          path="/viewer/dashboard"
          element={
            <ProtectedRoute allowedRoles={['viewer', 'parent']}>
              <ParentLayout>
                <ParentDashboard />
              </ParentLayout>
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Catch all route - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
